import type { FC } from "react";
import toast from "react-hot-toast";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  Stack,
  SvgIcon,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { thunks } from "src/thunks/content-types";
import { useDispatch, useSelector } from "src/store";
import { useTranslation } from "react-i18next";
import { tokens } from "src/locales/tokens";
import LoadingButton from "@mui/lab/LoadingButton";
import React, { useState } from "react";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { LoadIndicator } from "../common/load-indicator";
import { ErrorMessage } from "../common/error-message";

// Define the Zod schema
const createValidationSchema = (t: any) =>
  z.object({
    contentTypes: z.array(
      z.object({
        id: z.string().optional(),
        name: z
          .string()
          .max(255, t(tokens.general.validators.maxLength255) as string)
          .min(1, t(tokens.general.validators.required) as string),
        isTemporary: z.boolean().optional(),
      })
    ),
  });

// Define the Values type using Zod inference
type FormValues = z.infer<ReturnType<typeof createValidationSchema>>;

export const StrategyContentTypes: FC = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { strategyId } = useSelector((state) => state.settings);
  const { items, hasLoadingError, isLoading } = useSelector(
    (state) => state.contentTypes
  );

  // Create the validation schema
  const validationSchema = createValidationSchema(t);

  // Initialize the form
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      contentTypes: items || [],
    },
    resolver: zodResolver(validationSchema),
  });

  // Setup field array for contentTypes
  const { fields, append, remove } = useFieldArray({
    control,
    name: "contentTypes",
  });

  // Reset form when items change
  React.useEffect(() => {
    if (items) {
      reset({ contentTypes: items });
    }
  }, [items, reset]);

  if (isLoading) {
    return <LoadIndicator />;
  }

  if (hasLoadingError) {
    return (
      <Card>
        <CardContent>
          <ErrorMessage
            text={t(tokens.contentTypes.errorLoadingContentTypes)}
          />
        </CardContent>
      </Card>
    );
  }

  const onSubmit = async (data: FormValues): Promise<void> => {
    setIsSubmitting(true);
    try {
      await dispatch(
        thunks.updateContentTypes(strategyId!, {
          contentTypes: data.contentTypes,
        })
      );
      toast.success(t(tokens.contentTypes.form.successMessage));
    } catch (error) {
      toast.error(t(tokens.general.formError));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={4}>
        <Card sx={{ display: "flex", flexDirection: "column" }}>
          {fields.length === 0 && (
            <>
              <CardContent>
                <Typography variant="body2">
                  {t(tokens.contentTypes.form.noContentTypes)}
                </Typography>
              </CardContent>
              <Divider />
            </>
          )}
          <Box sx={{ overflowX: "auto" }}>
            <List disablePadding>
              {fields.map((field, index) => (
                <ListItem
                  key={field.id}
                  divider
                  sx={{ p: 2, width: "100%" }}
                >
                  <Stack
                    spacing={2}
                    direction={{
                      xs: "column",
                      sm: "row",
                    }}
                    alignItems={"end"}
                    sx={{
                      width: "100%",
                    }}
                  >
                    <Controller
                      name={`contentTypes.${index}.name`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          error={!!errors.contentTypes?.[index]?.name}
                          fullWidth
                          helperText={errors.contentTypes?.[index]?.name?.message}
                        />
                      )}
                    />
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent={"space-between"}
                      sx={{
                        width: {
                          xs: "100%",
                          sm: "50%",
                          md: "40%",
                        },
                      }}
                    >
                      <Controller
                        name={`contentTypes.${index}.isTemporary`}
                        control={control}
                        render={({ field: { value, onChange, ...field } }) => (
                          <FormControlLabel
                            control={
                              <Switch
                                {...field}
                                checked={!!value}
                                onChange={(e) => onChange(e.target.checked)}
                              />
                            }
                            label={t(tokens.contentTypes.form.type)}
                          />
                        )}
                      />

                      <IconButton onClick={() => remove(index)}>
                        <SvgIcon>
                          <DeleteOutlinedIcon color="error" />
                        </SvgIcon>
                      </IconButton>
                    </Stack>
                  </Stack>
                </ListItem>
              ))}

              <Box sx={{ p: 2 }}>
                <Button
                  onClick={() =>
                    append({
                      id: "",
                      name: "",
                      isTemporary: false,
                    })
                  }
                  variant="outlined"
                  color="primary"
                >
                  {t(tokens.general.buttons.add)}
                </Button>
              </Box>
            </List>
          </Box>
        </Card>

        <Stack
          alignItems="center"
          direction="row-reverse"
          justifyContent="flex-end"
          spacing={1}
        >
          <LoadingButton
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
            variant="contained"
          >
            {t(tokens.general.buttons.update)}
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
};
